import { useTranslation } from 'module/hooks/use-translation';
import { Editor } from '@tiptap/core';
import { EditorState } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';
import { CellSelection, TableMap } from '@tiptap/pm/tables';
import { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  canDeleteColumn,
  canDeleteRow,
} from 'components/organisms/document-editor/extensions/table/table-delete-guards';
import { TableHeaderPopover } from '../../../../../../components/organisms/document-editor/extensions/table/TableHeaderPopover';
import { Dropdown } from '../../dropdown/dropdown';
import { SelectionActionPopover } from '../editor-popover/selection-action-popover';
import { EditorPopoverContentProps } from '../editor-popover/types';
import { getCoordinatesFromSelection } from '../utils/editor-utils';

interface TableToolbarContentProps {
  editor: Editor;
  closePopover: () => void;
  togglePlacement?: (val: 'top' | 'bottom' | 'left' | 'right') => void;
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-02);
  gap: var(--spacing-01);
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-01) var(--spacing-02);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--background-color);
  color: var(--text-color);
  cursor: pointer;
  font-size: var(--font-size-sm);
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-hover-color);
    border-color: var(--border-hover-color);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: var(--border-color);
  margin: 0 var(--spacing-01);
`;

export enum TableCellType {
  ROW = 'ROW',
  COLUMN = 'COLUMN',
  CELL = 'CELL',
}

export enum TableDataType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN',
  SingleSelect = 'SINGLE_SELECT',
  MultiSelect = 'MULTI_SELECT',
}

const ColumnIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zm2 2h2v10H7V7zm4 0h2v10h-2V7zm4 0h2v10h-2V7z" />
  </svg>
);

const RowIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zm2 2h10v2H7V7zm0 4h10v2H7v-2zm0 4h10v2H7v-2z" />
  </svg>
);

export function findTable(selection) {
  let $pos = selection.$anchor;

  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);

    if (node.type.spec.tableRole === 'table') {
      return {
        node,
        pos: $pos.before(d),
        start: $pos.start(d),
        depth: d,
      };
    }
  }

  return null;
}

export function resolveCellStart(state: EditorState) {
  const { selection, doc } = state;

  if (selection instanceof CellSelection) {
    return selection.$anchorCell;
  }

  // Walk up to the nearest cell/header_cell and resolve its start
  for (let d = selection.$anchor.depth; d > 0; d--) {
    const role = selection.$anchor.node(d)?.type?.spec?.tableRole;

    if (role === 'cell' || role === 'header_cell') {
      return doc.resolve(selection.$anchor.before(d));
    }
  }

  return null;
}

/** Returns { pos, node } for the header cell of the current column, or null */
export function getHeaderCellOfCurrentColumn(state: EditorState) {
  const table = findTable(state.selection);

  if (!table) {
    return null;
  }

  const $cell = resolveCellStart(state);

  if (!$cell) {
    return null;
  }

  const map = TableMap.get(table.node);

  // Position of the current cell measured from table.start
  const cellPosInTable = $cell.pos - table.start;

  // Rect handles col/row spans correctly
  const rect = map.findCell(cellPosInTable); // { top, left, bottom, right }
  const colIndex = rect.left;

  // Position of the (row 0, current col) cell inside the table node
  const headerCellPosInTable = map.positionAt(0, colIndex, table.node);

  // Convert to document position
  const pos = table.start + headerCellPosInTable;
  const node = table.node.nodeAt(headerCellPosInTable);

  return node ? { pos, node } : null;
}

export function getHeaderCellOfColumn(
  state: EditorState,
  direction: 'left' | 'right',
) {
  const table = findTable(state.selection);

  if (!table) {
    return null;
  }

  const $cell = resolveCellStart(state);

  if (!$cell) {
    return null;
  }

  const map = TableMap.get(table.node);

  // Position of the current cell measured from table.start
  const cellPosInTable = $cell.pos - table.start;

  // Rect handles col/row spans correctly
  const rect = map.findCell(cellPosInTable); // { top, left, bottom, right }
  const colIndex = rect.left;

  const newColIndex = direction === 'left' ? -1 : +1;
  // Position of the (row 0, current col) cell inside the table node
  const headerCellPosInTable = map.positionAt(
    0,
    colIndex + (colIndex > 0 ? newColIndex : 0),
    table.node,
  );

  // Convert to document position
  const pos = table.start + headerCellPosInTable;
  const node = table.node.nodeAt(headerCellPosInTable);

  return node ? { pos, node } : null;
}

function getCurrentCellPos(state) {
  const { selection } = state;

  for (let d = selection.$anchor.depth; d > 0; d--) {
    const role = selection.$anchor.node(d)?.type?.spec?.tableRole;

    if (role === 'cell' || role === 'header_cell') {
      return selection.$anchor.before(d);
    }
  }

  return null;
}

export function getHeaderCellOfRow(
  state: EditorState,
  direction: 'above' | 'below',
) {
  const table = findTable(state.selection);

  if (!table) {
    return null;
  }

  const $cell = resolveCellStart(state);

  if (!$cell) {
    return null;
  }

  const map = TableMap.get(table.node);

  // Position of the current cell measured from table.start
  const cellPosInTable = $cell.pos - table.start;

  // Rect handles col/row spans correctly
  const rect = map.findCell(cellPosInTable); // { top, left, bottom, right }
  const rowIndex = rect.top;

  const newRowIndex = direction === 'above' ? -1 : +1;
  // Position of the (current row, col 0) cell inside the table node
  const headerCellPosInTable = map.positionAt(
    rowIndex + newRowIndex,
    0,
    table.node,
  );

  // Convert to document position
  const pos = table.start + headerCellPosInTable;
  const node = table.node.nodeAt(headerCellPosInTable);

  return node ? { pos, node } : null;
}

export const addColumnAfterAndFocus = (editor: any) => () => {
  const { state, tr, dispatch } = editor;

  // Step 1: Add column
  editor.commands.addColumnAfter();

  // Step 2: Find table info
  const table = findTable(state.selection);

  if (!table) {
    return false;
  }

  const map = TableMap.get(table.node);

  // Find current cell position after adding
  const cellStart = getCurrentCellPos(state);
  const rect = map.findCell(cellStart - table.start);

  // Step 3: Move to the new column's first row (row 0)
  const newColIndex = rect.right; // because we inserted AFTER current col
  const headerCellPosInTable = map.positionAt(0, newColIndex, table.node);
  const docPos = table.start + headerCellPosInTable;

  // Step 4: Create a text selection inside the cell
  const textPos = docPos + 1; // position inside the cell content

  tr.setSelection(TextSelection.create(tr.doc, textPos));
  if (dispatch) {
    dispatch(tr);
  }

  return true;
};

function checkIfCellIsHeader(
  state: EditorState,
  rowIndex: number,
  colIndex: number,
): boolean {
  const table = findTable(state.selection);

  if (!table) {
    return false;
  }

  const map = TableMap.get(table.node);
  const cellPosInTable = map.positionAt(rowIndex, colIndex, table.node);
  const node = table.node.nodeAt(cellPosInTable);

  return node?.type?.spec?.tableRole === 'header_cell';
}

export const TableToolbarContent: FC<TableToolbarContentProps> = ({
  editor,
  closePopover,
  togglePlacement,
}) => {
  const [showHeaderPopover, setShowHeaderPopover] = useState(false);
  const [direction, setDirection] = useState<
    'left' | 'right' | 'above' | 'below'
  >('left');
  const [headerType, setHeaderType] = useState<
    TableCellType.ROW | TableCellType.COLUMN
  >(TableCellType.COLUMN);

  // Translation keys
  const columnLabel = useTranslation('table_toolbar.column');
  const rowLabel = useTranslation('table_toolbar.row');
  const insertColumnLeftLabel = useTranslation(
    'table_toolbar.insert_column_left',
  );
  const insertColumnRightLabel = useTranslation(
    'table_toolbar.insert_column_right',
  );
  const deleteColumnLabel = useTranslation('table_toolbar.delete_column');
  const insertRowAboveLabel = useTranslation('table_toolbar.insert_row_above');
  const insertRowBelowLabel = useTranslation('table_toolbar.insert_row_below');
  const deleteRowLabel = useTranslation('table_toolbar.delete_row');

  const handleCommand = useCallback(
    (command: () => boolean) => {
      const success = command();

      if (success) {
        editor.commands.focus();
        const headerCell = getHeaderCellOfCurrentColumn(editor.state);

        if (headerCell) {
          setShowHeaderPopover(true);
        }

        closePopover();
      }
    },
    [closePopover, editor.state, editor.commands],
  );

  const handleHeaderSave = useCallback(
    (columnName, columnType, aiContext, _headerType) => {
      setShowHeaderPopover(false);
      togglePlacement?.('top');
      if (headerType === TableCellType.COLUMN) {
        switch (direction) {
          case 'left': {
            editor.commands.addColumnBefore();

            break;
          }

          case 'right':
            editor.commands.addColumnAfter();
            break;
        }

        if (direction === 'left' || direction === 'right') {
          const headerCell = getHeaderCellOfColumn(editor.state, direction);

          if (headerCell) {
            const tr = editor.state.tr;

            // Update the node attributes using setNodeMarkup
            tr.setNodeMarkup(headerCell.pos, undefined, {
              ...headerCell.node.attrs,
              config: {
                name: columnName,
                dataType: columnType.value,
                aiContext: aiContext,
                cellType: TableCellType.COLUMN,
              },
            });

            const textNode = headerCell.node.firstChild;

            if (textNode && textNode.text !== columnName) {
              tr.replaceWith(
                headerCell.pos + 1,
                headerCell.pos + 1 + textNode.nodeSize,
                editor.state.schema.text(columnName),
              );
            }

            editor.view.dispatch(tr);
          }
        }
      } else {
        switch (direction) {
          case 'above':
            editor.commands.addRowBefore();
            break;
          case 'below':
            editor.commands.addRowAfter();
            break;
        }

        if (direction === 'above' || direction === 'below') {
          const headerCell = getHeaderCellOfRow(editor.state, direction);

          if (headerCell) {
            const tr = editor.state.tr;

            // Update the node attributes using setNodeMarkup
            tr.setNodeMarkup(headerCell.pos, undefined, {
              ...headerCell.node.attrs,
              config: {
                name: columnName,
                aiContext: aiContext,
                cellType: TableCellType.ROW,
              },
            });

            const textNode = headerCell.node.firstChild;

            if (textNode && textNode.text !== columnName) {
              tr.replaceWith(
                headerCell.pos + 1,
                headerCell.pos + 1 + textNode.nodeSize,
                editor.state.schema.text(columnName),
              );
            }

            editor.view.dispatch(tr);
            editor.commands.focus();
          }
        }
      }

      closePopover();
    },
    [
      closePopover,
      direction,
      editor.commands,
      editor.state,
      editor.view,
      headerType,
      togglePlacement,
    ],
  );

  const handleInsertColumnLeft = useCallback(() => {
    setShowHeaderPopover(true);
    togglePlacement?.('bottom');
  }, [togglePlacement]);

  const handleHeaderCancel = useCallback(() => {
    setShowHeaderPopover(false);
    togglePlacement?.('top');
  }, [togglePlacement]);

  const handleClickOutside = useCallback(() => {
    setShowHeaderPopover(false);
    togglePlacement?.('top');
  }, [togglePlacement]);

  const isTableActive = editor.isActive('table');

  if (!isTableActive) {
    return null;
  }

  // If showing header popover, render it instead of the toolbar
  if (showHeaderPopover) {
    // Create a mock node for the new column header
    const mockNode = {
      attrs: {
        config: {
          name: '',
          dataType: TableDataType.TEXT,
          aiContext: '',
        },
      },
    };

    return (
      <TableHeaderPopover
        headerType={headerType}
        headerText=""
        node={mockNode}
        onSave={handleHeaderSave}
        onCancel={handleHeaderCancel}
        handleClickOutside={handleClickOutside}
      />
    );
  }

  // canShow() (below) only exposes this toolbar for plain data-cell selections
  // (never a tableHeader), so the selected column is never the header column
  // here — isTargetingHeaderColumn is correctly always false.
  const currentTable = findTable(editor.state.selection);
  const canDeleteColumnHere = currentTable
    ? canDeleteColumn(currentTable.node)
    : true;
  const canDeleteRowHere = currentTable
    ? canDeleteRow(currentTable.node)
    : true;

  return (
    <Wrapper>
      <Dropdown
        trigger={['click']}
        menu={{
          onMouseDown: (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
          onClick: ({ key, domEvent }) => {
            domEvent.preventDefault();
            domEvent.stopPropagation();
            switch (key) {
              case 'insert_column_left': {
                if (checkIfCellIsHeader(editor.state, 0, 1)) {
                  handleInsertColumnLeft();
                  setDirection('left');
                  setHeaderType(TableCellType.COLUMN);
                } else {
                  editor.commands.addColumnBefore();
                }
                break;
              }
              case 'insert_column_right':
                if (checkIfCellIsHeader(editor.state, 0, 1)) {
                  handleInsertColumnLeft();
                  setDirection('right');
                  setHeaderType(TableCellType.COLUMN);
                } else {
                  editor.commands.addColumnAfter();
                }
                break;
              case 'delete_column':
                if (!canDeleteColumnHere) {
                  break;
                }
                handleCommand(() => editor.commands.deleteColumn());
                break;
            }
          },
          items: [
            {
              type: 'group',
              label: columnLabel,
              children: [
                {
                  key: 'insert_column_left',
                  label: insertColumnLeftLabel,
                },
                {
                  key: 'insert_column_right',
                  label: insertColumnRightLabel,
                },
                {
                  key: 'delete_column',
                  label: deleteColumnLabel,
                  disabled: !canDeleteColumnHere,
                },
              ],
            },
          ],
        }}
      >
        <Button>
          <ColumnIcon />
          <span style={{ marginLeft: '4px', fontSize: '12px' }}>▼</span>
        </Button>
      </Dropdown>

      <Divider />

      <Dropdown
        trigger={['click']}
        menu={{
          onMouseDown: (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
          onClick: ({ key, domEvent }) => {
            domEvent.preventDefault();
            domEvent.stopPropagation();
            switch (key) {
              case 'insert_row_above':
                if (checkIfCellIsHeader(editor.state, 2, 0)) {
                  handleInsertColumnLeft();
                  setDirection('above');
                  setHeaderType(TableCellType.ROW);
                } else {
                  editor.commands.addRowBefore();
                }
                break;
              case 'insert_row_below':
                if (checkIfCellIsHeader(editor.state, 1, 0)) {
                  handleInsertColumnLeft();
                  setDirection('below');
                  setHeaderType(TableCellType.ROW);
                } else {
                  editor.commands.addRowAfter();
                }
                break;
              case 'delete_row':
                if (!canDeleteRowHere) {
                  break;
                }
                handleCommand(() => editor.commands.deleteRow());
                break;
            }
          },
          items: [
            {
              type: 'group',
              label: rowLabel,
              children: [
                {
                  key: 'insert_row_above',
                  label: insertRowAboveLabel,
                },
                {
                  key: 'insert_row_below',
                  label: insertRowBelowLabel,
                },
                {
                  key: 'delete_row',
                  label: deleteRowLabel,
                  disabled: !canDeleteRowHere,
                },
              ],
            },
          ],
        }}
      >
        <Button>
          <RowIcon />
          <span style={{ marginLeft: '4px', fontSize: '12px' }}>▼</span>
        </Button>
      </Dropdown>
    </Wrapper>
  );
};

interface TableToolbarProps {
  editor: Editor;
}

export const TableToolbar: FC<TableToolbarProps> = ({ editor }) => {
  const renderContent = useCallback(
    ({ editor, closePopover }: EditorPopoverContentProps) => (
      <TableToolbarContent editor={editor} closePopover={closePopover} />
    ),
    [],
  );

  const canShow = useCallback((editor: Editor) => {
    // First check if we're in a table
    if (!editor.isActive('table')) {
      return false;
    }

    // Check if the current selection is in a table header
    const { selection } = editor.state;
    const $pos = selection.$from;

    // Walk up the tree to find if we're in a tableHeader node
    for (let depth = $pos.depth; depth >= 0; depth--) {
      const node = $pos.node(depth);

      if (node.type.name === 'tableHeader') {
        // Selection is in a table header, don't show the toolbar
        return false;
      }
    }

    // Selection is in a table but not in a header, show the toolbar
    return true;
  }, []);

  useEffect(() => {
    if (editor) {
      const handleSelectionUpdate = () => {
        // This will trigger the toolbar to re-evaluate
        editor.view.updateState(editor.view.state);
      };

      editor.on('selectionUpdate', handleSelectionUpdate);

      return () => {
        editor.off('selectionUpdate', handleSelectionUpdate);
      };
    }
  }, [editor]);

  return (
    <SelectionActionPopover
      editor={editor}
      content={renderContent}
      canShow={canShow}
      placement={'bottom'}
      getPosition={getCoordinatesFromSelection}
    />
  );
};
